import React from 'react';
import { Star } from 'lucide-react';

const HighlightsStrip = ({ highlights, onOpenWall }) => {
  if (!highlights || highlights.length === 0) return null;

  // Per-type badge palette for the unified tile layout below. The
  // POLL tile keeps its own inline styling (defined in the branch
  // further down) so it stays visually identical to the community
  // reference — this table only drives the birthday / update / memory
  // tiles that we're re-skinning to match it.
  const BADGE_STYLES = {
    birthday: { label: 'BIRTHDAY', bg: 'rgba(245,158,11,0.95)', fg: '#FFF', fallbackBg: 'linear-gradient(135deg, #F59E0B, #FBBF24)' },
    announcement: { label: 'UPDATE', bg: 'rgba(93,191,173,0.95)', fg: '#FFF', fallbackBg: 'linear-gradient(135deg, #5DBFAD, #7DD3C0)' },
    event_memory: { label: 'MEMORY', bg: 'rgba(16,185,129,0.95)', fg: '#FFF', fallbackBg: 'linear-gradient(135deg, #10B981, #34D399)' },
  };

  return (
    <div data-testid="highlights-strip" style={{ marginBottom: 16 }}>
      {/* Heading row — sits outside the bordered box. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px 8px' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Highlights</span>
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>{highlights.length} new</span>
      </div>

      {/* Bordered container — holds the horizontal scroller only. */}
      <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, background: '#FFF8F0', padding: '12px 0' }}>
      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '0 12px 4px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
        className="hide-scrollbar">
        {highlights.map((highlight) => {
          // Poll tile — matches the community `actionPolls` tile visuals:
          // 95x115 rectangular card, gradient / image background, POLL
          // badge top-left, optional +X star badge top-right, bold white
          // title clamped to two lines at the bottom.
          if (highlight.type === 'poll') {
            return (
              <div key={highlight.id} onClick={() => onOpenWall(highlight)}
                data-testid={`poll-tile-${highlight.eventId || highlight.id}`}
                style={{ flexShrink: 0, width: 95, height: 115, borderRadius: 12, overflow: 'hidden', position: 'relative', cursor: 'pointer', scrollSnapAlign: 'start',
                  background: highlight.image_url ? `url(${highlight.image_url}) center/cover` : 'linear-gradient(135deg, #F59E0B, #FBBF24)' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 55%)' }} />
                <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 9, fontWeight: 700, color: '#FFF', background: 'rgba(245,158,11,0.95)', padding: '2px 6px', borderRadius: 8, letterSpacing: 0.4 }}>{highlight.is_closed ? 'RESULTS' : 'POLL'}</span>
                {highlight.star_reward > 0 && (
                  <span data-testid={`poll-tile-star-${highlight.eventId || highlight.id}`}
                    style={{ position: 'absolute', top: 6, right: 6, fontSize: 9, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '2px 6px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <Star size={9} fill="#F59E0B" color="#F59E0B" />+{highlight.star_reward}
                  </span>
                )}
                <div style={{ position: 'absolute', bottom: 6, left: 6, right: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {highlight.title}
                  </div>
                </div>
              </div>
            );
          }
          // Unified tile — matches the POLL tile layout: full-bleed
          // image or gradient background, badge top-left, bold white
          // title clamped to two lines at the bottom. Uses each
          // highlight's existing image field (`user_photo` for
          // birthdays, `image_url` for announcements, `cover_image`
          // for event memories) with a per-type gradient fallback.
          const style = BADGE_STYLES[highlight.type] || BADGE_STYLES.announcement;
          const bgImg = highlight.type === 'birthday' ? highlight.user_photo
            : highlight.type === 'announcement' ? highlight.image_url
            : highlight.cover_image;
          const title = highlight.type === 'birthday'
            ? `Wishing ${highlight.user_name} a Very Happy Birthday`
            : highlight.label;
          return (
            <div key={highlight.id} onClick={() => onOpenWall(highlight)} data-testid={`highlight-${highlight.id}`}
              style={{ flexShrink: 0, width: 95, height: 115, borderRadius: 12, overflow: 'hidden', position: 'relative', cursor: 'pointer', scrollSnapAlign: 'start',
                background: bgImg ? `url(${bgImg}) center/cover` : style.fallbackBg }}>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 55%)' }} />
              <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 9, fontWeight: 700, color: style.fg, background: style.bg, padding: '2px 6px', borderRadius: 8, letterSpacing: 0.4 }}>{style.label}</span>
              <div style={{ position: 'absolute', bottom: 6, left: 6, right: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {title}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
};

export default HighlightsStrip;
